<template>
  <div class="seminis-portal" :class="{ open }">
    <div class="bg" @click="open = false" v-if="open"></div>
    <div class="top-bar">
      <div class="badge" @click="open = !open">
        <img src="./assets/leaf_icon.png" alt />
      </div>
      <div class="search-container" v-if="open">
        <label for="search">
          <font-awesome-icon :icon="['fas', 'search']"></font-awesome-icon>
        </label>
        <input type="search" name="search" id="search" placeholder="Search" v-model="searchTerm" />
      </div>
    </div>
    <div class="mid-bar" v-if="open && !searching">
      <ul class="services-list">
        <li
          v-for="service in services"
          :key="service.name"
          class="service"
          @click="selectService(service.slug)"
          :class="{
            active: activeService == service.slug,
            [service.slug]: true
          }"
        >
          <font-awesome-icon
            class="service-icon"
            :icon="[service.iconStyle, service.icon]"
            :title="service.name"
          ></font-awesome-icon>
          {{ service.name }}
        </li>
      </ul>
    </div>
    <div class="bottom-bar" v-if="open">
      <div class="manage-list" v-if="activeService == 'manage' && !searching">
        <div class="list-label">Manage Sites</div>
        <a
          v-for="site in manageSites"
          class="single-service-list-item"
          :href="site.link"
          target="_blank"
          :key="site.link"
        >
          <font-awesome-icon class="single-service-icon" :icon="getServiceIcon('manage')"></font-awesome-icon>
          <span class="single-service-name">{{ site.name }}</span>
        </a>
      </div>
      <div class="wordpress-list" v-if="activeService == 'wordpress' && !searching">
        <div class="list-label">WordPress Sites</div>
        <a
          v-for="site in wordpressSites"
          class="single-service-list-item"
          :href="site.link"
          target="_blank"
          :key="site.link"
        >
          <font-awesome-icon class="single-service-icon" :icon="getServiceIcon('wordpress')"></font-awesome-icon>
          <span class="single-service-name">{{ site.name }}</span>
        </a>
      </div>
      <div
        class="big-button-container"
        v-if="
          !searching && single_url_service_is_active
        "
      >
        <a
          class="big-button"
          :href="activeServiceObject.links[environment]"
        >Open {{ activeServiceObject.name }}</a>
      </div>
      <div class="search-list" v-if="searching">
        <a
          v-for="result in results"
          class="single-service-list-item"
          :href="result.link"
          target="_blank"
          :key="result.link"
        >
          <font-awesome-icon class="single-service-icon" :icon="getServiceIcon(result.service)"></font-awesome-icon>
          <span class="single-service-name">{{ result.name }}</span>
        </a>
        <div class="no-results-found" v-if="results.length == 0">No results found.</div>
      </div>
    </div>
  </div>
</template>

<script>
import productionSites from "./productionSites.json";
import qaSites from "./qaSites.json";

import FuzzySearch from "fuzzy-search";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faDatabase,
  faSignature,
  faFilePdf,
  faSearch,
  faFileSignature,
  faMapMarked
} from "@fortawesome/free-solid-svg-icons";
import { faWordpressSimple } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

library.add(faDatabase);
library.add(faSignature);
library.add(faFilePdf);
library.add(faWordpressSimple);
library.add(faSearch);
library.add(faFileSignature);
library.add(faMapMarked);

export default {
  name: "SeminisPortal",
  components: {
    FontAwesomeIcon
  },
  props: {
    environment: {
      type: String,
      default: "prod"
    }
  },
  data() {
    return {
      searchTerm: null,
      open: false,
      activeService: "manage",
      services: [
        {
          name: "Manage",
          slug: "manage",
          icon: "database",
          iconStyle: "fas",
          label: "Manage Sites",
          dataSource: "manageSites"
        },
        {
          name: "WordPress",
          slug: "wordpress",
          icon: "wordpress-simple",
          iconStyle: "fab",
          label: "WordPress Sites",
          dataSource: "wordpressSites"
        },
        {
          name: "Seedonym",
          slug: "seedonym",
          icon: "signature",
          iconStyle: "fas",
          label: "Seedonym",
          links: {
            prod: "https://seedonym.paradowski.site",
            qa: "https://seedonym.parado.cz"
          }
        },
        {
          name: "PDF Generator",
          slug: "pdf",
          icon: "file-pdf",
          iconStyle: "fas",
          label: "PDF Generator",
          links: {
            prod: "https://seminis-pdf-generator.paradowski.site",
            qa: "https://seminis-pdf-generator.parado.cz"
          }
        },
        {
          name: "Paralegal",
          slug: "paralegal",
          icon: "file-signature",
          iconStyle: "fas",
          label: "Paralegal",
          links: {
            prod: "https://paralegal.paradowski.site",
            qa: "https://paralegal.parado.cz"
          }
		},
		{
          name: "Dealers",
          slug: "dealers",
          icon: "map-marked",
          iconStyle: "fas",
          label: "Dealers",
          links: {
            prod: "https://seminis-dealers.paradowski.site/",
            qa: "https://seminis-dealers.parado.cz"
          }
        }
      ]
    };
  },
  computed: {
    manageSites() {
      return this.countrySites.map(site => ({
        name: site.name,
        link: site.manage_url,
        service: "manage"
      }));
    },
    wordpressSites() {
      return this.countrySites.map(site => ({
        name: site.name,
        link: `${site.website_url}/admin`,
        service: "wordpress"
      }));
    },
    activeServiceObject() {
      return this.getService(this.activeService);
    },
    single_url_service_is_active() {
      return (
        this.services
          .filter(service => !!service.links)
          .map(service => service.slug)
          .indexOf(this.activeService) > -1
      );
    },
    countrySites() {
      return this.environment == "prod" ? productionSites : qaSites;
    },
    searching() {
      return this.searchTerm && this.searchTerm.length > 0;
    },
    results() {
      if (!this.searching) return [];

      let haystack = this.manageSites.concat(this.wordpressSites);
      let searcher = new FuzzySearch(haystack, ["name", "link", "service"]);
      return searcher.search(this.searchTerm, { sort: true });
    }
  },
  methods: {
    getServiceIcon(serviceSlug) {
      let service = this.services.find(s => s.slug == serviceSlug);
      return [service.iconStyle, service.icon];
    },
    getService(serviceSlug) {
      return this.services.find(s => s.slug == serviceSlug);
    },
    selectService(serviceSlug) {
      this.searchTerm = null;
      this.activeService = serviceSlug;
    }
  }
};
</script>
<style lang="postcss" scoped>
.seminis-portal {
  font-size: 12px;
  font-family: Arial;
}

.seminis-portal.open {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 2;
  padding: 3em;
}
.badge {
  background-color: black;
  padding: 0.5em 0.25em;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 3em;
  box-sizing: border-box;
}

.badge > img {
  width: 30px;
  height: auto;
}

.top-bar,
.mid-bar,
.bottom-bar {
  background-color: #fff;
}

.top-bar {
  display: inline-block;
}

.open > .top-bar {
  display: flex;
  justify-content: space-between;
  align-content: center;
}

.search-container {
  background-color: #eee;
  padding: 1em 1.5em;
}

.bg {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: -1;
}

#search {
  appearance: none;
  border: 0;
  background-color: transparent;
  padding: 0 1em;
  outline: none;
}

.mid-bar {
  border-top: 1px solid #dadce0;
  border-bottom: 1px solid #dadce0;
}

.bottom-bar {
  overflow: auto;
  max-height: 60vh;
}
.services-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  font-size: 1.25em;
}
.service {
  padding: 0.5em 1em;
  color: #aaa;
  border-bottom: 2px solid transparent;
}
.service.active,
.service:hover {
  border-bottom-color: currentColor;
  color: #333;
}

.service-icon {
  padding: 0 0.25em;
}

.list-label {
  text-align: initial;
  padding: 0.5em 1.5em;
  border-bottom: 1px solid #dadce0;
}

.single-service-list-item {
  display: flex;
  text-align: initial;
  padding: 1em 1.5em;
  text-decoration: none;
  font-size: 1.3em;
  color: inherit;
}

.single-service-list-item:hover {
  background-color: #eee;
}

.single-service-name {
  display: inline-block;
  padding: 0 0.5em;
}

.big-button-container {
  padding: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.big-button {
  font-size: 3em;
  text-decoration: none;
  border-color: #333;
  padding: 1em 1.5em;
  border-radius: 5px;
  border: 3px solid;
  color: inherit;
}

.search-list {
  border-top: 1px solid #dadce0;
  min-height: 5em;
}

.no-results-found {
  margin-top: 2em;
}
</style>
